import React, { lazy } from 'react';
import '@/lib/errorReporter';
import { enableMapSet } from "immer";
enableMapSet();
import { StrictMode } from 'react'
import type { Root } from 'react-dom/client'
import { createRoot } from 'react-dom/client'
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { WithLoader } from '@/components/PageLoader';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import '@/index.css'
import { Toaster } from 'sonner';

const HomePage = lazy(async () => {
  const mod = await import('@/pages/HomePage');
  return { default: mod.HomePage };
});
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const BookingBoardPage = lazy(() => import('@/pages/BookingBoardPage'));
const HostView = lazy(() => import('@/pages/HostView'));
const InternBookingPage = lazy(() => import('@/pages/InternBookingPage'));
const ParticipantView = lazy(() => import('@/pages/ParticipantView'));
const SpectatorView = lazy(() => import('@/pages/SpectatorView'));
const QuizImportPreview = lazy(() => import('@/pages/QuizImportPreview'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const WrittenAssessmentAdmin = lazy(() => import('@/pages/WrittenAssessmentAdmin'));
const WrittenGradingPage = lazy(() => import('@/pages/WrittenGradingPage'));
const WrittenAssessmentPage = lazy(() => import('@/pages/WrittenAssessmentPage'));
const WrittenJoinPage = lazy(() => import('@/pages/WrittenJoinPage'));
const TrainingPlannerAdmin = lazy(() => import('@/pages/TrainingPlannerAdmin'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
const router = createBrowserRouter([
  {
    path: "/",
    element: <WithLoader><HomePage /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/login",
    element: <WithLoader><LoginPage /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin",
    element: <ProtectedRoute><WithLoader><AdminDashboard /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/library",
    element: <ProtectedRoute><WithLoader><AdminDashboard /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/history",
    element: <ProtectedRoute><WithLoader><AdminDashboard /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/bookings",
    element: <ProtectedRoute><WithLoader><BookingBoardPage /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/settings",
    element: <ProtectedRoute><WithLoader><AdminDashboard /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/import-review",
    element: <ProtectedRoute><WithLoader><QuizImportPreview /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/host/:sessionId",
    element: <ProtectedRoute><WithLoader><HostView /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/play/:sessionId",
    element: <WithLoader><ParticipantView /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/bookings",
    element: <WithLoader><InternBookingPage /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/spectate/:sessionId",
    element: <WithLoader><SpectatorView /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/written",
    element: <ProtectedRoute><WithLoader><WrittenAssessmentAdmin /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/training-planner",
    element: <ProtectedRoute><WithLoader><TrainingPlannerAdmin /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin/written/grade/:sessionId",
    element: <ProtectedRoute><WithLoader><WrittenGradingPage /></WithLoader></ProtectedRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/written",
    element: <WithLoader><WrittenJoinPage /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/written/:sessionId",
    element: <WithLoader><WrittenAssessmentPage /></WithLoader>,
    errorElement: <RouteErrorBoundary />,
  },
]);

const root: Root = import.meta.env.DEV && (window as any).__ROOT__ 
  ? (window as any).__ROOT__ 
  : createRoot(document.getElementById('root')!);

if (import.meta.env.DEV) {
  (window as any).__ROOT__ = root;
}

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RouterProvider router={router} />
        <Toaster position="top-right" richColors toastOptions={{ style: { background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', borderRadius: '20px', border: '1px solid hsl(var(--border))' } }} />
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);
